
import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    insuranceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const insuranceTypes = [
    'Individual Health Insurance',
    'Family Health Insurance',
    'Group/Employer Insurance',
    'Medicare Supplement',
    'Short-Term Health Insurance',
    'Not Sure - Need Guidance'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        insuranceType: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Request Your Free Quote
          </h2>
          <p className="text-lg text-darkGray max-w-3xl mx-auto">
            Fill out the form below and one of our insurance experts will contact you within 24 hours
          </p>
        </div>

        <div className="bg-gradient-to-br from-lightBlue/5 to-white rounded-2xl p-8 md:p-12 border border-lightBlue/20 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-primary mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-lightBlue/30 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-primary mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-lightBlue/30 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-primary mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-lightBlue/30 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all text-sm"
                  placeholder="(214) 555-0123"
                />
              </div>

              <div>
                <label htmlFor="insuranceType" className="block text-sm font-semibold text-primary mb-2">
                  Insurance Type Interested In *
                </label>
                <select
                  id="insuranceType"
                  name="insuranceType"
                  value={formData.insuranceType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-lightBlue/30 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select insurance type...</option>
                  {insuranceTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-primary mb-2">
                Additional Information
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-lightBlue/30 focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all resize-none text-sm"
                placeholder="Tell us about your insurance needs, current coverage, family size, or any specific questions you have..."
              ></textarea>
              <div className="text-right text-xs text-darkGray mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                <div>
                  <h4 className="font-semibold text-green-800 mb-1">Thank you for contacting us!</h4>
                  <p className="text-sm text-green-700">
                    We've received your inquiry and one of our insurance experts will reach out to you within 24 hours.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
                <div>
                  <h4 className="font-semibold text-red-800 mb-1">Submission failed</h4>
                  <p className="text-sm text-red-700">
                    Please try again or call us directly at (214) 306-0121.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between pt-4">
              <p className="text-sm text-darkGray">
                <i className="ri-lock-line mr-2"></i>
                Your information is secure and confidential
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-secondary text-white rounded-lg font-semibold hover:bg-secondary/90 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    <span>Get Free Quote</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="mt-12 text-center">
          <p className="text-darkGray mb-4">Prefer to speak with someone right away?</p>
          <a
            href="tel:+12143060121"
            className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line text-xl"></i>
            <span>Call (214) 306-0121 Now</span>
          </a>
        </div>
      </div>
    </section>
  );
}
